import usePromise from "react-use-promise"
import { usePortal } from "../common/usePortal"
import { useEsriModules } from "../common/useEsriModules"

/**
 * Loads a FeatureLayer from portal
 * @param featureLayerId The ID of the feature layer you'd like to load
 */
export const useFeatureLayer = (featureLayerId: string | undefined) => {
  /** Get the things we need to make the query */
  const portal = usePortal()
  const [FeatureLayer, PortalQueryParams] = useEsriModules<
    [typeof __esri.FeatureLayer, typeof __esri.PortalQueryParams]
  >(["esri/layers/FeatureLayer", "esri/portal/PortalQueryParams"])

  return usePromise<__esri.FeatureLayer>(() => {
    /** If our things aren't ready, or there's no ID, resolve undefined */
    if (!portal || !FeatureLayer || !PortalQueryParams || !featureLayerId)
      return Promise.resolve(undefined)

    /** Make the query and load the feature layer */
    return portal
      .queryItems(
        new PortalQueryParams({
          query: `id:${featureLayerId}`,
        })
      )
      .then(({ results }) => {
        /** Generate FeatureLayer */
        const featureLayer = new FeatureLayer({
          url: results[0].url,
          id: results[0].id,
          outFields: ["*"],
          spatialReference: { wkid: 4326 },
        })

        /** Return the initialised feature layer */
        return featureLayer.load()
      })
  }, [portal, FeatureLayer, PortalQueryParams, featureLayerId])
}
